import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Delete, WarningAltFilled } from '@mint-lib/icons';
import {
  Button,
  Card,
  Popover,
  PopoverContent,
  PopoverTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { useCustomMetricsDrawer } from '../../contexts/CustomMetricsContext.jsx';
import { CheckboxWithLabel } from '../atoms/CheckboxWithLabel.jsx';
import { Tooltip } from '../atoms/Tooltip.jsx';

export const DeleteCustomMetricButton = () => {
  const { t } = useTranslation('@myn/import-hub');
  const {
    isAddMetricMode,
    selectedMetricDraft,
    submitDeleteMetric,
    isDeletePopoverOpen,
    isDrawerAnimating,
    openDeletePopover,
    closeDeletePopover,
  } = useCustomMetricsDrawer();

  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);
  const handleIsDeleteConfirmedChange = (checked: boolean) => {
    setIsDeleteConfirmed(checked);
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      await submitDeleteMetric();
      closeDeletePopover();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeDeletePopover();
    }
  };

  return (
    <Popover
      open={isDeletePopoverOpen && !isDrawerAnimating}
      onOpenChange={handleOpenChange}
    >
      <Tooltip
        asChild
        description={
          selectedMetricDraft.isUsed
            ? t(`This custom metric cannot be deleted as it's used as a KPI`)
            : ''
        }
      >
        <div>
          <PopoverTrigger asChild>
            <Button
              type="button"
              onClick={openDeletePopover}
              disabled={isAddMetricMode || Boolean(selectedMetricDraft.isUsed)}
              variant="text"
              color="error"
              size="large"
              data-dd-action-name="Delete"
            >
              <Delete />
              {t('Delete')}
            </Button>
          </PopoverTrigger>
        </div>
      </Tooltip>
      <PopoverContent className="z-popover" side="bottom" align="start">
        <Card className="flex w-[480px] flex-col" contentClassName="p-5">
          <h4
            className={cn(
              typographyVariants({ variant: 'h4' }),
              'mb-8 flex items-center gap-2',
            )}
          >
            <WarningAltFilled className="fill-text-error" />
            {t('Deleting a custom metric is irreversible!')}
          </h4>
          <p
            className={cn(
              typographyVariants({ variant: 'bodyLong01' }),
              'mb-4',
            )}
          >
            {t(
              `By proceeding you will permanently remove the selected custom metric from our system. All data associated with this metric will be permanently deleted from the Reporting.`,
            )}
          </p>
          <CheckboxWithLabel
            label={t('I understand I will not be able to recover the data')}
            checked={isDeleteConfirmed}
            onCheckedChange={handleIsDeleteConfirmedChange}
          />
          <div className="mt-6 flex justify-end">
            <Button
              type="button"
              onClick={closeDeletePopover}
              variant="text"
              size="large"
              data-dd-action-name="Cancel"
            >
              {t('Cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={!isDeleteConfirmed || isSubmitting}
              variant="contained"
              color="error"
              size="large"
              className="ml-2"
              data-dd-action-name="Yes, delete forever"
            >
              {t('Yes, delete forever')}
            </Button>
          </div>
        </Card>
      </PopoverContent>
    </Popover>
  );
};
